import { TablePageLayout } from '@/layouts/TablePageLayout';
import { formatRelativeTime } from '@/utils/format';
import CrownOutlined from '@ant-design/icons/CrownOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import SwapOutlined from '@ant-design/icons/SwapOutlined';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import App from 'antd/es/app';
import Space from 'antd/es/space';
import { AxiosError } from 'axios';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  DataTable,
  type DataTableColumnType,
  StatusTag,
  createActionColumn,
  useDataTableState,
} from 'ui-components';
import type { ActionItem } from 'ui-components';

import { useSessionUser } from '../../../contexts/SessionUserContext';
import {
  useDeleteTeamUser,
  useTeamUsers,
  useUpdateTeamUser,
} from '../../../queries/team-users';
import { useUsersByIds } from '../../../queries/users';

interface TeamUserRow extends Record<string, unknown> {
  teamUserId: string;
  teamId: string;
  userId: string;
  role: 'admin' | 'member';
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  userName?: string;
  email?: string | null;
}

export const TeamMembersPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { message, modal } = App.useApp();

  const { requestState } = useDataTableState({
    filterMap: {
      name: 'nameLike',
      email: 'emailLike',
      role: (value) => ({ roles: [value] }),
    },
  });

  const {
    data: teamUsersData,
    isLoading: isLoadingTeamUsers,
    error,
  } = useTeamUsers(
    teamId ? { ...requestState, teamIds: [teamId] } : undefined,
    {
      enabled: !!teamId,
    },
  );

  const userIds = useMemo(() => {
    if (!teamUsersData) return [];
    return [...new Set(teamUsersData.items.map((tu) => tu.userId))];
  }, [teamUsersData]);

  const { data: usersData, isLoading: isLoadingUsers } = useUsersByIds(
    userIds,
    { enabled: userIds.length > 0 },
  );

  const deleteTeamUserMutation = useDeleteTeamUser();
  const updateTeamUserMutation = useUpdateTeamUser();

  const tableData = useMemo(() => {
    if (!teamUsersData) return [];

    const userMap = new Map(
      usersData?.items.map((user) => [user.userId, user]) || [],
    );

    return teamUsersData.items.map(
      (teamUser): TeamUserRow => ({
        ...teamUser,
        userName: userMap.get(teamUser.userId)?.fullName,
        email: teamUser.email,
      }),
    );
  }, [teamUsersData, usersData]);

  const isLoading = isLoadingTeamUsers || isLoadingUsers;

  // Calculate the number of admins in the team
  const adminCount = useMemo(() => {
    if (!teamUsersData) return 0;
    return teamUsersData.items.filter((tu) => tu.role === 'admin').length;
  }, [teamUsersData]);

  // Get the current user's ID from session
  const { userId: sessionUserId } = useSessionUser();

  const handleInviteMember = () => {
    // TODO: Implement invite member functionality
    // This will be implemented in a future update
  };

  const handleRemoveMember = useCallback(
    (teamUserId: string, userId: string) => {
      void modal.confirm({
        title: 'Remove Team Member',
        content: `Are you sure you want to remove user ${userId} from the team?`,
        okText: 'Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await deleteTeamUserMutation.mutateAsync(teamUserId);
            void message.success('Team member removed successfully');
          } catch (error) {
            const errorMessage =
              error instanceof AxiosError
                ? error.response?.data?.error?.message ||
                  'Failed to remove team member'
                : 'Failed to remove team member';
            void message.error(errorMessage);
          }
        },
      });
    },
    [deleteTeamUserMutation, message, modal],
  );

  const handleChangeRole = useCallback(
    (
      teamUserId: string,
      userId: string,
      userName: string | undefined,
      newRole: 'admin' | 'member',
    ) => {
      const displayName = userName || userId;
      const roleText = newRole === 'admin' ? 'an admin' : 'a member';

      void modal.confirm({
        title: 'Change User Role',
        content: `Are you sure you want to make ${displayName} ${roleText}?`,
        okText: 'Change Role',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await updateTeamUserMutation.mutateAsync({
              teamUserId,
              data: { role: newRole },
            });
            void message.success(`Successfully changed role to ${newRole}`);
          } catch (error) {
            const errorMessage =
              error instanceof AxiosError
                ? error.response?.data?.error?.message
                : undefined;
            if (errorMessage) {
              void message.error(errorMessage);
            } else {
              void message.error('Failed to change user role');
            }
          }
        },
      });
    },
    [updateTeamUserMutation, message, modal],
  );

  const getMemberActions = useCallback(
    (record: TeamUserRow): (ActionItem | { type: 'divider' })[] => {
      // Check if this is the current user
      const isSelf = record.userId === sessionUserId;

      // Check if this would be the last admin
      const isLastAdmin = adminCount === 1 && record.role === 'admin';

      const changeRoleDisabled = isLastAdmin && record.role === 'admin';
      const removeDisabled = isSelf || isLastAdmin;

      return [
        {
          key: 'change-role',
          icon: <SwapOutlined />,
          label: record.role === 'admin' ? 'Make Member' : 'Make Admin',
          onClick: () =>
            handleChangeRole(
              record.teamUserId,
              record.userId,
              record.userName,
              record.role === 'admin' ? 'member' : 'admin',
            ),
          disabled: changeRoleDisabled,
        },
        { type: 'divider' },
        {
          key: 'remove',
          icon: <DeleteOutlined />,
          label: 'Remove from Team',
          danger: true,
          onClick: () => handleRemoveMember(record.teamUserId, record.userId),
          disabled: removeDisabled,
        },
      ];
    },
    [adminCount, handleChangeRole, handleRemoveMember, sessionUserId],
  );

  const columns: DataTableColumnType<TeamUserRow>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'userName',
        key: 'name',
        render: (_, record: TeamUserRow) => (
          <Space>
            <UserOutlined />
            <span>{record.userName || record.userId}</span>
          </Space>
        ),
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (email: string | null) => email || '-',
        filter: {
          type: 'search',
          placeholder: 'Search by email...',
        },
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        render: (role: string) => {
          const isAdmin = role === 'admin';
          return (
            <StatusTag status={role} type="generic">
              <Space>
                {isAdmin ? <CrownOutlined /> : <UserOutlined />}
                {isAdmin ? 'Admin' : 'Member'}
              </Space>
            </StatusTag>
          );
        },
        sorter: true,
        filter: {
          type: 'select',
          placeholder: 'All Roles',
          options: [
            { label: 'Admin', value: 'admin' },
            { label: 'Member', value: 'member' },
          ],
        },
      },
      {
        title: 'Joined',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (date: string) => formatRelativeTime(new Date(date)),
        sorter: true,
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (date: string) => formatRelativeTime(new Date(date)),
        sorter: true,
      },
      createActionColumn<TeamUserRow>({
        getActions: getMemberActions,
        width: 80,
        align: 'right',
      }),
    ],
    [getMemberActions],
  );

  if (error) {
    return (
      <div>
        <h2>Error Loading Team Members</h2>
        <p>There was an error loading the team members. Please try again.</p>
      </div>
    );
  }
  return (
    <TablePageLayout
      title="Team Members"
      description="Manage your team members and their roles."
      actions={[
        {
          label: 'Invite Member',
          type: 'primary',
          icon: <UserAddOutlined />,
          onClick: handleInviteMember,
        },
      ]}
    >
      <DataTable<TeamUserRow>
        columns={columns}
        data={tableData}
        loading={isLoading}
        rowKey="teamUserId"
        maxVisibleFilters={3}
        pagination={{
          current: teamUsersData?.pagination?.page || 1,
          pageSize: teamUsersData?.pagination?.pageSize || 20,
          total: teamUsersData?.pagination?.totalItems || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} members`,
        }}
        emptyState={{
          icon: <UserOutlined />,
          title: 'No team members yet',
          description: 'Invite your first team member to collaborate.',
          action: {
            label: 'Invite Member',
            onClick: handleInviteMember,
            icon: <UserAddOutlined />,
          },
          overrideOnFiltersPresent: {
            title: 'No members match your filters',
            description: 'Try adjusting your filters to see more results.',
            action: undefined,
          },
        }}
      />
    </TablePageLayout>
  );
};
